import React, { useState } from 'react';
import { Button as BootstrapButton, type ButtonProps, ListGroup, Modal } from 'react-bootstrap';
import type { FuLocale } from '../../i18n/fuLocale';
import './CommandHistoryButton.css';
import { formatFabulaUltimaCommandForDisplay } from '@common/FabulaUltimaCommand';

// This panel only uses ordinary buttons; avoid the polymorphic JSX union with 3D elements.
const Button: React.ComponentType<ButtonProps> = BootstrapButton;

export type StateTimelineItem = {
  commandString: string;
  isSensitive: boolean;
  redactedCommandString: string;
  timestamp: number;
  initiatorName: string;
};

interface CommandHistoryButtonProps {
  stateTimeline: StateTimelineItem[];
  position: number;
  onMove: (position: number) => void;
  onRollback: (position: number) => void;
  isHost: boolean;
  locale?: FuLocale;
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString();
}

function getDisplayText(entry: StateTimelineItem, isHost: boolean): string {
  return formatFabulaUltimaCommandForDisplay(entry.isSensitive && !isHost ? entry.redactedCommandString : entry.commandString);
}

function getCopyText(entry: StateTimelineItem, isHost: boolean): string {
  // Preserve executable image commands, while respecting the existing player redaction.
  return entry.isSensitive && !isHost ? entry.redactedCommandString : entry.commandString;
}

export const CommandHistoryButton: React.FC<CommandHistoryButtonProps> = ({
  stateTimeline,
  position,
  onMove,
  onRollback,
  isHost,
  locale = 'zh',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copyStatus, setCopyStatus] = useState<{ key: string; success: boolean } | null>(null);
  const copyLabel = locale === 'zh' ? '复制命令' : 'Copy command';

  return (
    <>
      <Button
        variant="secondary"
        size="lg"
        className="command-history-button"
        onClick={() => { setCopyStatus(null); setShowModal(true); }}
        title="查看状态时间线"
      >
        状
      </Button>

      <Modal className="fu-modal" show={showModal} onHide={() => { setShowModal(false); setCopyStatus(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>状态时间线</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button variant="outline-secondary" disabled={!isHost || position === 0} onClick={() => onMove(position - 1)}>
              ↶ 回退一步
            </Button>
            <span className="text-muted">可回退 {position} 步 · 可前进 {stateTimeline.length - position} 步</span>
            <Button variant="outline-secondary" disabled={!isHost || position === stateTimeline.length} onClick={() => onMove(position + 1)}>
              前进一步 ↷
            </Button>
          </div>
          {isHost && position < stateTimeline.length && (
            <div className="d-flex justify-content-center mb-3">
              <Button variant="danger" onClick={() => { if (window.confirm("确认回溯到当前预览节点？之后的状态变更将被删除。")) onRollback(position); }}>确认回溯</Button>
            </div>
          )}
          <ListGroup>
            <ListGroup.Item
              action={isHost}
              className={`state-timeline-item ${position === 0 ? 'state-timeline-item--applied' : 'state-timeline-item--future'}`}
              onClick={isHost ? () => onMove(0) : undefined}
            >
              <strong>{locale === 'zh' ? '第 0 条 · 初始/上传状态' : 'Entry 0 · Initial/uploaded state'}</strong>
              <span className={`float-end badge state-timeline-status ${position === 0 ? 'state-timeline-status--applied' : 'state-timeline-status--future'}`}>
                {position === 0 ? '已执行' : '可前进'}
              </span>
            </ListGroup.Item>
              {stateTimeline.map((entry, index) => {
                const isApplied = index < position;
                const key = `${entry.timestamp}-${index}`;
                const commandContent = <>
                  <code className="state-timeline-command">{getDisplayText(entry, isHost)}</code>
                  <div className="small text-muted mt-1">
                    {formatTime(entry.timestamp)}
                    {entry.initiatorName && <span className="ms-2">{entry.initiatorName}</span>}
                  </div>
                </>;
                return (
                <ListGroup.Item
                  key={key}
                  className={`state-timeline-item ${isApplied ? 'state-timeline-item--applied' : 'state-timeline-item--future'}`}
                >
                  <div className="d-flex align-items-start gap-2">
                    {isHost ? <button type="button" className="state-timeline-select" onClick={() => onMove(index + 1)}>
                      {commandContent}
                    </button> : <div className="state-timeline-content">{commandContent}</div>}
                    <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
                      <button type="button" className="btn btn-sm btn-outline-secondary state-timeline-copy"
                        aria-label={locale === 'zh' ? `复制第 ${index + 1} 条命令` : `Copy command ${index + 1}`}
                        title={copyLabel} onClick={async event => {
                          event.stopPropagation();
                          try {
                            if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
                            await navigator.clipboard.writeText(getCopyText(entry, isHost));
                            setCopyStatus({ key, success: true });
                          } catch {
                            setCopyStatus({ key, success: false });
                          }
                        }}>
                        {copyStatus?.key === key && copyStatus.success ? (locale === 'zh' ? '已复制' : 'Copied') : copyLabel}
                      </button>
                      <span className={`badge state-timeline-status ${isApplied ? 'state-timeline-status--applied' : 'state-timeline-status--future'}`}>
                        {isApplied ? '已执行' : '可前进'}
                      </span>
                    </div>
                  </div>
                  {copyStatus?.key === key && !copyStatus.success && <small role="alert" className="d-block mt-1 text-danger">
                    {locale === 'zh' ? '复制失败，请检查浏览器剪贴板权限。' : 'Copy failed. Check browser clipboard permissions.'}
                  </small>}
                </ListGroup.Item>
                );
              })}
          </ListGroup>
        </Modal.Body>
      </Modal>
    </>
  );
};
